import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { OrgUserCcPicker } from "@/components/OrgUserCcPicker";
import { PHONE_DIALOG_FOOTER, PHONE_SHEET_DIALOG } from "@/lib/responsive";

/** Shown right after a Manager clicks Approve on a travel request - but
 *  the request is NOT approved yet at this point. This dialog's own Send
 *  button is what actually approves it (calls approveAndNotifyFinance,
 *  which decides the Manager stage, routes to Finance, and sends both the
 *  Teams notification and the sign-off email together, in one atomic
 *  call - see MyApprovals.tsx's sendFinanceNotice). Closing this dialog
 *  early (refresh, closed tab) therefore leaves the request exactly as it
 *  was - still Pending - rather than silently approved with no one
 *  notified, which is what an earlier "approve first, popup just gates
 *  the email" design allowed to happen.
 *
 *  Send is the only way to actually APPROVE: no backdrop/outside click, no
 *  Escape, and the only × this renders calls onClose, a true cancel - it
 *  closes the dialog and nothing else, same as if it had never been
 *  opened (no DB write has happened yet at this point either way - see
 *  above). The Manager retries by clicking Approve again from the queue,
 *  same ORIGINAL confirm dialog as before. Deliberately NOT built on the
 *  shared ConfirmDialog: its shape here (a read-only To line + a Cc
 *  picker, not a single optional comment) is different enough, and
 *  ConfirmDialog always renders a Cancel button and forwards no way to
 *  suppress DialogContent's own × either. onPointerDownOutside/
 *  onInteractOutside/onEscapeKeyDown below still block every dismiss path
 *  OTHER than that one explicit × click. */
export function FinanceNoticeDialog({
  open,
  onClose,
  toName,
  toEmail,
  cc,
  onCcChange,
  onSend,
  sending,
  error
}: {
  open: boolean;
  /** Called only by the dialog's own × - a true cancel (see above), never
   *  fired by an outside click or Escape, both still blocked below. */
  onClose: () => void;
  toName: string | null;
  toEmail: string | null;
  cc: string[];
  onCcChange: (cc: string[]) => void;
  onSend: () => void;
  sending: boolean;
  error?: string | null;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className={PHONE_SHEET_DIALOG}
        closeClassName="text-destructive opacity-100 hover:text-destructive hover:bg-destructive/10"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Approve &amp; notify Finance</DialogTitle>
          <DialogDescription>
            Review who gets the sign-off email, then click Send to approve this
            request and notify Finance (email and Teams).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>To</Label>
            <div className="min-w-0 rounded-md border border-input bg-muted/40 px-3 py-2 text-sm">
              {/* truncate: a multi-word name (e.g. a middle initial as its
                  own word, "Dharma Bharath M") was wrapping at the normal
                  space boundary in a narrow phone-width dialog, stranding
                  that last word alone on its own line - truncating to one
                  line with an ellipsis instead keeps the name intact-
                  looking rather than visually broken apart. */}
              <p className="truncate" title={toName || undefined}>
                {toName || "No Finance Approver configured"}
              </p>
              {/* break-all: a long, hyphen-less domain (e.g. a tenant's
                  *.onmicrosoft.com address) has no natural wrap point
                  either, so it's allowed to wrap at any character instead
                  of overflowing the box. */}
              {toEmail && (
                <p className="break-all text-muted-foreground">{toEmail}</p>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Cc (optional)</Label>
            <OrgUserCcPicker
              value={cc}
              onChange={onCcChange}
              excludeEmail={toEmail}
              disabled={sending}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter className={PHONE_DIALOG_FOOTER}>
          <Button onClick={onSend} disabled={sending}>
            {sending ? "Approving…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
